import { getPhaserGameSync } from '../PhaserGame'
import type Game from '../scenes/Game'
import { Message, type TeleportTarget } from '../../../types/Messages'
import store from '../stores'
import { addNotification } from '../stores/NotificationStore'
import { recordRecentTeleport } from '../stores/TeleportStore'
import {
  evaluateTeleportRedundancy,
  type TeleportRedundancyReason,
  type TeleportRedundancyResult,
} from '../../../types/teleportEligibility'

export { evaluateTeleportRedundancy }
export type { TeleportRedundancyReason, TeleportRedundancyResult }

/**
 * Which virtual room a player is currently standing in ('' / undefined = open floor). Injected
 * rather than imported: the area-detection service's import chain reaches DOM-only code, and this
 * module is imported by the network layer, so a direct import would drag it into every consumer.
 * Game.ts registers the real resolver on scene create.
 */
export type TeleportPlayerRoomResolver = (playerId: string) => string | undefined
let resolvePlayerRoomId: TeleportPlayerRoomResolver = () => undefined
export function setTeleportPlayerRoomResolver(resolver: TeleportPlayerRoomResolver): void {
  resolvePlayerRoomId = resolver
}

const NOT_REDUNDANT: TeleportRedundancyResult = { redundant: false, reason: null }

/** One wording for the UI hint, the client-side guard and the server's same_room/too_close denial. */
export function teleportRedundancyMessage(reason: TeleportRedundancyReason | null | undefined): string {
  return reason === 'too_close' ? "You're already close to them" : "You're already there"
}

/**
 * SC-425: would teleporting to `target` be a no-op right now? Reads the live scene (own pose, the
 * target's rendered pose + room) and defers the rule itself to the shared evaluator the server also
 * uses. Unknown target pose (not rendered / outside the viewport) is treated as *not* redundant —
 * the server is the backstop and holds the authoritative positions.
 */
export function getMyTeleportPose(sceneOverride?: Game): { x: number; y: number; roomId: string } | undefined {
  const scene = sceneOverride ?? (getPhaserGameSync()?.scene?.keys?.game as Game | undefined)
  const me = scene?.myPlayer
  if (!me) return undefined
  return {
    x: me.x,
    y: me.y,
    roomId: resolvePlayerRoomId(me.playerId) ?? '',
  }
}

export function getTeleportRedundancy(target: TeleportTarget, sceneOverride?: Game): TeleportRedundancyResult {
  const scene = sceneOverride ?? (getPhaserGameSync()?.scene?.keys?.game as Game | undefined)
  const requester = getMyTeleportPose(scene)
  if (!requester) return NOT_REDUNDANT

  if (target.type === 'room') {
    return evaluateTeleportRedundancy(requester, { type: 'room', roomId: target.roomId })
  }
  const other = (scene as unknown as { otherPlayerMap?: Map<string, { x: number; y: number }> }).otherPlayerMap?.get(target.sessionId)
  if (!other) return NOT_REDUNDANT
  const roomId = resolvePlayerRoomId(target.sessionId) ?? ''
  return evaluateTeleportRedundancy(requester, { type: 'player', player: { x: other.x, y: other.y, roomId } })
}

// The most recently requested target, recorded as "recent" only once the server confirms it —
// see confirmPendingTeleport/clearPendingTeleport, called from MessageHandler.ts's TELEPORTED and
// TELEPORT_DENIED handlers. No per-request correlation id: recents are deduped by target identity
// anyway, so if a second request fires before the first's response arrives, the eventual
// confirm/deny gets (mis)attributed to the newer one — rare (the server's 500ms double-click
// guard covers same-target repeats) and self-corrects on the next teleport.
let pendingTeleportTarget: TeleportTarget | null = null

/**
 * Single send-site for TELEPORT_REQUEST, shared by the popup, both teleport-picker tabs, and the
 * in-world room-name click. Keeps recents-recording and the message shape from drifting apart
 * between surfaces (SC-394/396's "one shared rule for wording and behavior" requirement).
 */
export function requestTeleport(target: TeleportTarget): boolean {
  // Guests are zone-restricted and only move when their host escorts/admits them. Silent, since a
  // guest double-clicking a room is ordinary exploring, not a request worth a toast.
  if (store.getState().guest?.guestContext) return false
  const phaserGame = getPhaserGameSync()
  const game = phaserGame?.scene?.keys?.game as Game | undefined
  const room = game?.network?.room
  if (!room) {
    store.dispatch(addNotification({ message: 'Not connected to a room', type: 'error', duration: 5000 }))
    return false
  }
  // Same rule the server enforces; checked here too so a stale/disabled-looking surface (hover
  // label, recents) gets instant feedback instead of a round trip.
  const redundancy = getTeleportRedundancy(target, game)
  if (redundancy.redundant) {
    store.dispatch(addNotification({ message: teleportRedundancyMessage(redundancy.reason), type: 'info', duration: 3000 }))
    return false
  }
  pendingTeleportTarget = target
  room.send(Message.TELEPORT_REQUEST, { target })
  return true
}

/** Called from MessageHandler.ts on a confirmed TELEPORTED — records only a real success. */
export function confirmPendingTeleport(): void {
  if (pendingTeleportTarget) {
    const userId = pendingTeleportTarget.type === 'player'
      ? resolveUserId(pendingTeleportTarget.sessionId)
      : undefined
    store.dispatch(recordRecentTeleport({ target: pendingTeleportTarget, userId }))
  }
  pendingTeleportTarget = null
}

/** The traveler's stable Back4App user id for a player target, read off the live roster cache —
 *  lets a recorded "recent" survive that person's next reconnect (see TeleportStore.ts). */
function resolveUserId(sessionId: string): string | undefined {
  const phaserGame = getPhaserGameSync()
  const game = phaserGame?.scene?.keys?.game as Game | undefined
  return game?.network?.players?.get(sessionId)?.userId || undefined
}

/** Called from MessageHandler.ts on TELEPORT_DENIED — clears without recording. */
export function clearPendingTeleport(): void {
  pendingTeleportTarget = null
}
